import { useMutation } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { useToast } from '@givebutter/primitives/toast';
import { updateAccount } from 'api/account';
import * as Form from 'components/form';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInvalidateUserAndAccount } from 'customHooks/useInvalidateUserAndAccount';
import { useTaxDeductibleSettings } from 'customHooks/useTaxDeductibleSettings';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface TaxDeductibilitySettingsValues {
    enabled: boolean;
    message: string;
}

const TaxDeductibilitySettings = () => {
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { mutate: invalidateUserData } = useInvalidateUserAndAccount();

    const {
        defaultMessage,
        enabledSetting: enabledStoredValue,
        messageSetting: messageStoredValue,
    } = useTaxDeductibleSettings();

    const methods = Form.useGivebutterForm<TaxDeductibilitySettingsValues>({
        defaultValues: {
            enabled: enabledStoredValue?.active ?? false,
            message: messageStoredValue?.value ?? defaultMessage,
        },
    });

    const {
        control,
        formState: { isDirty },
        path,
        handleSubmit,
        reset,
        watch,
    } = methods;

    const enabled = watch('enabled');

    const { mutate: submit, isPending: isLoading } = useMutation<
        unknown,
        GivebutterAxiosError,
        TaxDeductibilitySettingsValues
    >({
        mutationFn: ({ enabled, message }) => {
            return updateAccount(accountId, {
                settings: [
                    {
                        code: Settings.EnableTaxDeductibility.Code,
                        value: enabled,
                        active: enabled,
                        name: 'show_tax_deductibility',
                        type: 'boolean',
                    },
                    {
                        code: Settings.TaxDeductibiltyMessage.Code,
                        value: message ?? null,
                        active: enabled && !!message,
                        name: 'tax_deductibility_message',
                        type: 'text',
                    },
                ],
            });
        },
        onSuccess: (_response, values) => {
            success('Settings saved successfully!');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
            reset(values);
            invalidateUserData();
        },
        onError: e => {
            error(e.response?.data.message ?? 'Something went wrong. Please try again.');
        },
    });

    return (
        <Box className="flex flex-col gap-8 p-8">
            <div>
                <h3 className="text-2xl font-medium">Tax Deductibility Settings</h3>
                <p>
                    Show tax-deductible amounts on receipts and end-of year summaries when donations
                    include goods or services, like event tickets or auction items.
                </p>
            </div>
            <Form.Provider {...methods}>
                <form
                    className="flex flex-col items-start gap-6"
                    onSubmit={handleSubmit(values => submit(values))}
                >
                    <Form.Container className="flex-row justify-between">
                        <div className="flex flex-col">
                            <Form.Label name={path('enabled')}>Enable Tax Deductibility</Form.Label>
                            <p>
                                Adds a deductible amount and tax language to receipts when a fair
                                market value is provided.
                            </p>
                        </div>
                        <Form.UseFormSwitch name="enabled" control={control} />
                    </Form.Container>

                    {enabled && (
                        <Form.Container>
                            <Form.Label name={path('message')}>
                                Tax Deductible Disclaimer Message
                            </Form.Label>
                            <Form.UseFormTextArea
                                className="leading-snug"
                                name="message"
                                control={control}
                                rows={5}
                                autoResize
                            />
                        </Form.Container>
                    )}

                    <Form.SubmitButton isSubmitting={isLoading} disabled={!isDirty}>
                        Save
                    </Form.SubmitButton>
                </form>
            </Form.Provider>
        </Box>
    );
};

export { TaxDeductibilitySettings };
